import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ChevronLeftIcon, ChevronRightIcon, CheckCircleIcon, ArrowDownTrayIcon } from '@heroicons/react/24/outline';
import {
  CHAPTERS, CHAPTER_START_PAGES, CHAPTER_OVI_COUNTS, BOOK_PAGE_COUNT, CONTENT_PUBLIC_URL,
} from '@dnyaneshwari/shared';
import type { Ovi, ChapterShloka, TextCorrection, ReviewProgress } from '@dnyaneshwari/shared';

/** oviText keys an ovi by its zero-padded number, as the app reads it. */
const oviTextKey = (n: number) => String(n).padStart(3, '0');
import {
  getReviewPageUrl, reviewPagesPresent, uploadReviewPages,
  getCorrections, getAllCorrections, saveCorrection, removeCorrection,
  getChapterOviText, saveChapterOviText, clearOviTextCorrection,
  getReviewProgress, getAllReviewProgress, setReviewProgress,
} from '@/services/adminService';

const CONTENT_BASE = (import.meta.env.VITE_CONTENT_BASE_URL as string | undefined) ?? CONTENT_PUBLIC_URL;

/** One row of the reading order: a shloka sits before the ovi it introduces. */
type Item =
  | { kind: 'ovi'; id: string; number: number; text: string; ovi: Ovi }
  | { kind: 'shloka'; id: string; number: number; text: string; shloka: ChapterShloka };

function shlokaId(chapterId: string, n: number): string {
  return `s-${chapterId}-${String(n).padStart(3, '0')}`;
}

/** The text a shloka row edits: speaker line (if any) then the Sanskrit, one per line. */
function shlokaText(s: ChapterShloka): string {
  return s.speaker ? `${s.speaker}\n${s.textSanskrit}` : s.textSanskrit;
}

function buildItems(chapterId: string, ovis: Ovi[], shlokas: ChapterShloka[]): Item[] {
  const byBefore = new Map<number, ChapterShloka[]>();
  for (const s of shlokas) {
    const list = byBefore.get(s.beforeOvi) ?? [];
    list.push(s);
    byBefore.set(s.beforeOvi, list);
  }
  const items: Item[] = [];
  for (const o of ovis) {
    for (const s of byBefore.get(o.oviNumber) ?? []) {
      items.push({ kind: 'shloka', id: shlokaId(chapterId, s.shlokaNumber), number: s.shlokaNumber, text: shlokaText(s), shloka: s });
    }
    items.push({ kind: 'ovi', id: o.id, number: o.oviNumber, text: o.textMarathi, ovi: o });
  }
  return items;
}

export default function ReviewPage() {
  const [chapterId, setChapterId] = useState('01');
  const [items, setItems] = useState<Item[]>([]);
  const [loading, setLoading] = useState(false);
  const [corrections, setCorrections] = useState<Map<string, TextCorrection>>(new Map());
  const [progress, setProgress] = useState<ReviewProgress | null>(null);
  const [allProgress, setAllProgress] = useState<Map<string, ReviewProgress>>(new Map());

  // Book pane
  const [page, setPage] = useState<number>(CHAPTER_START_PAGES[0]);
  const [pageInput, setPageInput] = useState(String(CHAPTER_START_PAGES[0]));
  const [pageUrl, setPageUrl] = useState<string | null>(null);
  const [pagesPresent, setPagesPresent] = useState<boolean | null>(null);
  const urlCache = useRef(new Map<number, string | null>());
  const [zoom, setZoom] = useState(1);

  // Upload
  const [uploadState, setUploadState] = useState<{ done: number; total: number } | null>(null);

  // Editing
  const [editing, setEditing] = useState<string | null>(null);
  const [draft, setDraft] = useState('');
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);
  const [filter, setFilter] = useState('');

  const chapterNum = parseInt(chapterId, 10);

  // ---- load chapter text + corrections + progress
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setEditing(null);
    (async () => {
      const [chRes, shRes, corr, prog, live] = await Promise.all([
        fetch(`${CONTENT_BASE}/chapters/${chapterId}.json`),
        fetch(`${CONTENT_BASE}/chapters/${chapterId}.shlokas.json`),
        getCorrections(chapterId),
        getReviewProgress(chapterId),
        getChapterOviText(chapterId),
      ]);
      if (cancelled) return;
      const ch = chRes.ok ? ((await chRes.json()) as { ovis: Ovi[] }) : { ovis: [] };
      const sh = shRes.ok ? ((await shRes.json()) as { shlokas: ChapterShloka[] }) : { shlokas: [] };
      const built = buildItems(chapterId, ch.ovis, sh.shlokas);
      setItems(built);
      // What the app actually shows is oviText, and the ओव्या tab writes there
      // too. So the live store is read first and wins: otherwise this screen
      // would show its own record of a verse that had since been corrected
      // elsewhere, and the two screens would quietly disagree.
      const map = new Map(corr.map((c) => [c.id, c]));
      for (const item of built) {
        if (item.kind !== 'ovi') continue;
        const text = live[oviTextKey(item.number)];
        if (!text || text === item.text) continue;
        const had = map.get(item.id);
        map.set(item.id, {
          ...(had ?? { id: item.id, kind: 'ovi', chapterId, number: item.number, original: item.text, updatedBy: 'ओव्या tab' }),
          text,
          updatedAt: had?.updatedAt ?? new Date(),
        } as TextCorrection);
      }
      setCorrections(map);
      setProgress(prog);
      const startPage = prog?.page ?? CHAPTER_START_PAGES[chapterNum - 1] ?? 1;
      setPage(startPage);
      setPageInput(String(startPage));
      setLoading(false);
    })();
    return () => { cancelled = true; };
  }, [chapterId, chapterNum]);

  useEffect(() => {
    reviewPagesPresent().then(setPagesPresent);
    getAllReviewProgress().then((list) => setAllProgress(new Map(list.map((p) => [p.chapterId, p]))));
  }, []);

  // ---- page image
  useEffect(() => {
    if (pagesPresent === false) return;
    let cancelled = false;
    const cached = urlCache.current.get(page);
    if (cached !== undefined) { setPageUrl(cached); return; }
    setPageUrl(null);
    getReviewPageUrl(page).then((u) => {
      urlCache.current.set(page, u);
      if (!cancelled) setPageUrl(u);
    });
    // Warm the next page so turning feels instant.
    if (page < BOOK_PAGE_COUNT && !urlCache.current.has(page + 1)) {
      getReviewPageUrl(page + 1).then((u) => urlCache.current.set(page + 1, u));
    }
    return () => { cancelled = true; };
  }, [page, pagesPresent]);

  const goToPage = useCallback((n: number) => {
    const clamped = Math.min(BOOK_PAGE_COUNT, Math.max(1, n));
    setPage(clamped);
    setPageInput(String(clamped));
    void setReviewProgress(chapterId, { page: clamped });
  }, [chapterId]);

  // Arrow keys turn pages when nothing is being edited.
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (editing) return;
      const tag = (e.target as HTMLElement)?.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return;
      if (e.key === 'ArrowRight') goToPage(page + 1);
      if (e.key === 'ArrowLeft') goToPage(page - 1);
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [page, editing, goToPage]);

  // ---- upload
  async function handleUpload(files: FileList | null) {
    if (!files || files.length === 0) return;
    setUploadState({ done: 0, total: files.length });
    const n = await uploadReviewPages(Array.from(files), (done, total) => setUploadState({ done, total }));
    setUploadState(null);
    urlCache.current.clear();
    setPagesPresent(n > 0);
  }

  // ---- editing
  function startEdit(item: Item) {
    setEditing(item.id);
    setDraft(corrections.get(item.id)?.text ?? item.text);
    setNote(corrections.get(item.id)?.note ?? '');
  }

  async function saveEdit(item: Item) {
    const text = draft.trim();
    if (!text) return;
    setSaving(true);
    try {
      if (text === item.text) {
        // Back to the bundle's text: that is "no correction", not a correction equal to the original.
        if (corrections.has(item.id)) await removeCorrection(item.id);
        if (item.kind === 'ovi') await clearOviTextCorrection(chapterId, item.number);
        setCorrections((m) => { const n = new Map(m); n.delete(item.id); return n; });
      } else {
        // An ovi goes to oviText, which every installed app reads at its next
        // open. Corrections used to land only in textCorrections, which the
        // app has never read: they reached a reader solely if someone
        // exported them, folded them into the bundle and shipped a release,
        // which never happened. A shloka still has nowhere live to go -- the
        // Sanskrit ships inside the app -- so it keeps the record alone.
        if (item.kind === 'ovi') await saveChapterOviText(chapterId, { [oviTextKey(item.number)]: text });
        const c = { id: item.id, kind: item.kind, chapterId, number: item.number, original: item.text, text, ...(note.trim() ? { note: note.trim() } : {}) };
        await saveCorrection(c);
        setCorrections((m) => new Map(m).set(item.id, { ...c, updatedAt: new Date(), updatedBy: 'me' }));
      }
      setEditing(null);
    } finally {
      setSaving(false);
    }
  }

  async function discardCorrection(item: Item) {
    await removeCorrection(item.id);
    if (item.kind === 'ovi') await clearOviTextCorrection(chapterId, item.number);
    setCorrections((m) => { const n = new Map(m); n.delete(item.id); return n; });
    setEditing(null);
  }

  async function markVerified(upTo: number) {
    const next = progress?.verifiedUpTo === upTo ? 0 : upTo;
    await setReviewProgress(chapterId, { verifiedUpTo: next });
    const p = { chapterId, page, verifiedUpTo: next, updatedAt: new Date() };
    setProgress(p);
    setAllProgress((m) => new Map(m).set(chapterId, p));
  }

  async function exportCorrections() {
    const all = await getAllCorrections();
    const blob = new Blob([JSON.stringify(all, null, 2)], { type: 'application/json' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = 'corrections.json';
    a.click();
    URL.revokeObjectURL(a.href);
  }

  const totalOvis = useMemo(() => items.filter((i) => i.kind === 'ovi').length, [items]);
  const verifiedUpTo = progress?.verifiedUpTo ?? 0;
  const visible = useMemo(() => {
    const q = filter.trim();
    if (!q) return items;
    const n = parseInt(q, 10);
    if (Number.isInteger(n)) return items.filter((i) => i.number === n);
    return items.filter((i) => (corrections.get(i.id)?.text ?? i.text).includes(q));
  }, [items, filter, corrections]);

  return (
    <div className="h-[calc(100vh-3rem)] flex flex-col">
      <div className="flex items-start justify-between gap-4 mb-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Text review</h1>
          <p className="text-sm text-gray-500 mt-1">
            The book on the left, the app's text on the right. Click any ovi or shloka to correct it; corrections are saved as you go and folded into the app on the next content publish.
          </p>
        </div>
        <button onClick={exportCorrections} className="flex items-center gap-1.5 px-3 py-2 border border-gray-200 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 whitespace-nowrap">
          <ArrowDownTrayIcon className="w-4 h-4" /> Export corrections
        </button>
      </div>

      {/* Chapter strip with progress */}
      <div className="flex flex-wrap gap-1.5 mb-4">
        {CHAPTERS.map((ch) => {
          const id = String(ch.number).padStart(2, '0');
          const p = allProgress.get(id);
          const done = p?.verifiedUpTo ?? 0;
          const total = CHAPTER_OVI_COUNTS[ch.number - 1] ?? 0;
          const pct = total ? Math.round((done / total) * 100) : 0;
          const active = id === chapterId;
          return (
            <button
              key={id}
              onClick={() => setChapterId(id)}
              title={`${ch.titleMarathi}: ${done}/${total} verified`}
              className={`relative overflow-hidden px-2.5 py-1 rounded-md text-xs font-semibold border ${active ? 'border-amber-500 text-amber-800 bg-amber-50' : 'border-gray-200 text-gray-600 bg-white hover:bg-gray-50'}`}
            >
              <span className="absolute left-0 top-0 bottom-0 bg-green-100" style={{ width: `${pct}%` }} />
              <span className="relative">{ch.number}{pct === 100 ? ' ✓' : pct > 0 ? ` ${pct}%` : ''}</span>
            </button>
          );
        })}
      </div>

      <div className="flex-1 min-h-0 grid grid-cols-2 gap-4">
        {/* ---- Book pane */}
        <div className="bg-white rounded-xl border border-gray-200 flex flex-col min-h-0">
          <div className="flex items-center gap-2 px-3 py-2 border-b border-gray-200">
            <button onClick={() => goToPage(page - 1)} className="p-1.5 rounded-lg hover:bg-gray-100" title="Previous page (←)">
              <ChevronLeftIcon className="w-5 h-5 text-gray-600" />
            </button>
            <form onSubmit={(e) => { e.preventDefault(); goToPage(parseInt(pageInput, 10) || page); }} className="flex items-center gap-1 text-sm">
              <span className="text-gray-500">Page</span>
              <input
                value={pageInput}
                onChange={(e) => setPageInput(e.target.value)}
                onBlur={() => goToPage(parseInt(pageInput, 10) || page)}
                className="w-14 px-2 py-1 border border-gray-200 rounded-md text-center outline-none focus:border-amber-400"
                inputMode="numeric"
              />
              <span className="text-gray-400">/ {BOOK_PAGE_COUNT}</span>
            </form>
            <button onClick={() => goToPage(page + 1)} className="p-1.5 rounded-lg hover:bg-gray-100" title="Next page (→)">
              <ChevronRightIcon className="w-5 h-5 text-gray-600" />
            </button>
            <button onClick={() => goToPage(CHAPTER_START_PAGES[chapterNum - 1])} className="ml-1 px-2 py-1 text-xs font-medium text-amber-700 bg-amber-50 rounded-md hover:bg-amber-100">
              Chapter start (p. {CHAPTER_START_PAGES[chapterNum - 1]})
            </button>
            <div className="ml-auto flex items-center gap-1 text-xs text-gray-500">
              Zoom
              <input type="range" min={0.6} max={2.5} step={0.1} value={zoom} onChange={(e) => setZoom(parseFloat(e.target.value))} className="w-24" />
            </div>
          </div>

          <div className="flex-1 min-h-0 overflow-auto bg-gray-100">
            {pagesPresent === false ? (
              <div className="p-8 text-center text-sm text-gray-600 max-w-md mx-auto">
                <p className="font-semibold text-gray-800 mb-2">Book pages not uploaded yet</p>
                <p className="mb-4">
                  Pick the folder <code className="bg-gray-200 px-1 rounded">scripts/source/pages</code> from the project. It holds the 512 rendered pages of the Gita Press edition. They upload to a private, admin-only location.
                </p>
                {uploadState ? (
                  <p className="text-amber-700 font-medium">Uploading {uploadState.done} / {uploadState.total}…</p>
                ) : (
                  <label className="inline-block px-4 py-2 bg-amber-600 text-white rounded-lg text-sm font-semibold cursor-pointer hover:bg-amber-700">
                    Choose folder
                    <input
                      type="file"
                      multiple
                      accept="image/jpeg"
                      // Non-standard but supported by every desktop browser we use.
                      {...({ webkitdirectory: '' } as Record<string, string>)}
                      className="hidden"
                      onChange={(e) => handleUpload(e.target.files)}
                    />
                  </label>
                )}
              </div>
            ) : pageUrl ? (
              <img src={pageUrl} alt={`Page ${page}`} style={{ width: `${zoom * 100}%` }} className="block mx-auto shadow-md" />
            ) : pageUrl === null && pagesPresent ? (
              <div className="p-8 text-center text-sm text-gray-400">Loading page {page}…</div>
            ) : null}
          </div>
        </div>

        {/* ---- App text pane */}
        <div className="bg-white rounded-xl border border-gray-200 flex flex-col min-h-0">
          <div className="flex items-center gap-2 px-3 py-2 border-b border-gray-200">
            <select
              value={chapterId}
              onChange={(e) => setChapterId(e.target.value)}
              className="px-2 py-1 border border-gray-200 rounded-md text-sm outline-none focus:border-amber-400"
            >
              {CHAPTERS.map((ch) => (
                <option key={ch.number} value={String(ch.number).padStart(2, '0')}>{ch.number}. {ch.titleMarathi}</option>
              ))}
            </select>
            <input
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              placeholder="Jump to number or find text"
              className="flex-1 px-2 py-1 border border-gray-200 rounded-md text-sm outline-none focus:border-amber-400"
            />
            <span className="text-xs text-gray-500 whitespace-nowrap">
              {verifiedUpTo}/{totalOvis} verified · {corrections.size} corrected
            </span>
          </div>

          <div className="flex-1 min-h-0 overflow-auto">
            {loading ? (
              <div className="p-8 text-center text-sm text-gray-400">Loading chapter…</div>
            ) : (
              visible.map((item) => {
                const c = corrections.get(item.id);
                const isEditing = editing === item.id;
                const verified = item.kind === 'ovi' && item.number <= verifiedUpTo;
                const shown = c?.text ?? item.text;
                return (
                  <div
                    key={item.id}
                    className={`border-b border-gray-100 px-3 py-2 ${item.kind === 'shloka' ? 'bg-amber-50/60' : verified ? 'bg-green-50/50' : ''} ${c ? 'border-l-4 border-l-orange-400' : ''}`}
                  >
                    <div className="flex items-start gap-2">
                      <span className={`mt-0.5 shrink-0 text-[11px] font-semibold w-14 ${item.kind === 'shloka' ? 'text-amber-700' : 'text-gray-400'}`}>
                        {item.kind === 'shloka' ? `श्लोक ${item.number}` : `ओवी ${item.number}`}
                      </span>
                      {isEditing ? (
                        <div className="flex-1">
                          <textarea
                            value={draft}
                            onChange={(e) => setDraft(e.target.value)}
                            rows={Math.max(3, draft.split('\n').length + 1)}
                            autoFocus
                            className="w-full px-2 py-1.5 border border-amber-300 rounded-md text-base leading-relaxed font-marathi outline-none focus:border-amber-500"
                          />
                          <input
                            value={note}
                            onChange={(e) => setNote(e.target.value)}
                            placeholder="Note (optional): what was wrong"
                            className="w-full mt-1 px-2 py-1 border border-gray-200 rounded-md text-xs outline-none focus:border-amber-400"
                          />
                          <div className="flex gap-2 mt-1.5">
                            <button onClick={() => saveEdit(item)} disabled={saving || !draft.trim()} className="px-3 py-1 bg-amber-600 text-white rounded-md text-xs font-semibold disabled:opacity-50">{saving ? 'Saving…' : 'Save'}</button>
                            <button onClick={() => setEditing(null)} className="px-3 py-1 border border-gray-200 rounded-md text-xs">Cancel</button>
                            {c && <button onClick={() => discardCorrection(item)} className="ml-auto px-3 py-1 text-red-600 text-xs">Discard correction</button>}
                          </div>
                        </div>
                      ) : (
                        <button onClick={() => startEdit(item)} className="flex-1 text-left">
                          <p className={`whitespace-pre-line leading-relaxed font-marathi ${item.kind === 'shloka' ? 'text-amber-900 text-[15px]' : 'text-gray-800 text-base'}`}>{shown}</p>
                          {c && <p className="text-[11px] text-orange-700 mt-0.5">Corrected{c.note ? `: ${c.note}` : ''} · was: <span className="line-through">{c.original.slice(0, 60)}</span></p>}
                        </button>
                      )}
                      {item.kind === 'ovi' && !isEditing && (
                        <button
                          onClick={() => markVerified(item.number)}
                          title={verifiedUpTo === item.number ? 'Un-mark' : `Everything up to ovi ${item.number} matches the book`}
                          className={`shrink-0 p-1 rounded-md ${verifiedUpTo === item.number ? 'text-green-600 bg-green-100' : 'text-gray-300 hover:text-green-600 hover:bg-green-50'}`}
                        >
                          <CheckCircleIcon className="w-5 h-5" />
                        </button>
                      )}
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
